import "./Checkout.css";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useEffect, useMemo, useState } from "react";
import {
  createProductOrder,
  createPreorderOrder,
  fetchOrderById,
  fetchPreorderOrderDetails,
  submitQrPaymentProof,
  submitPreorderQrPaymentProof,
} from "../../api/orders.api";
import {
  fetchProfileDashboard,
  hasRequiredAddressInfo,
  hasRequiredPersonalInfo,
} from "../../api/profile.api";

const getFirstStringValue = (...values) =>
  values.find((value) => typeof value === "string" && value.trim());

const getQrTextValue = (details) =>
  getFirstStringValue(
    details?.qr_text,
    details?.qr_data,
    details?.upi_uri,
    details?.upi_url,
    details?.qr_upi_id,
    details?.qr_code,
    details?.qr
  );

const getQrPayeeName = (details) =>
  getFirstStringValue(
    details?.account_holder_name,
    details?.account_name,
    details?.upi_name,
    details?.beneficiary_name,
    details?.merchant_name,
    details?.name
  );

const getQrContactNumber = (details) =>
  getFirstStringValue(
    details?.phone_number,
    details?.mobile_number,
    details?.contact_number,
    details?.payment_number,
    details?.number
  );

const resolveIsMember = (profile) => {
  const membership = profile?.membership;
  if (!membership) return false;
  const status = membership?.status;
  if (typeof status === "string") {
    return status.toLowerCase() === "active";
  }
  return true;
};

const NON_MEMBER_POLICY_ITEMS = [
  {
    text: "Kindly note : Pls make sure to mention your phone number & address without fail .",
  },
  {
    text: "Shipping for the clearance sale will start from July 20th Only . Please make a purchase only if you have patience to wait . Kindly avoid DM asking for updates. We will contact you when it is ready.",
  },
  {
    text: "DIT / LIT / Factory QC issues are NOT COVERED at any circumstances",
  },
  {
    text: "Shipping costs are charged extra . Please ensure to make purchase in a single bill to avoid multiple charges. Each bill will be charged separately for delivery & cannot be clubbed.",
  },
  {
    text: "NO CANCELLATION, REFUND & RETURNS OR EXCHANGE",
  },
];

const MEMBER_POLICY_ITEMS = [
  {
    title: "Returns & Exchanges",
    text: "All sales are final. We do not offer cancellations, exchanges, or returns once a purchase is made.",
  },
  {
    title: "Damages",
    text: "Damage in transit is NOT covered",
  },
  {
    title: "Lost Shipments",
    text: "We are not responsible for packages lost in transit, as these circumstances are beyond our control.",
  },
  {
    title: "Shipping Schedule",
    text: "We ship every Wednesday. Orders placed on or after Wednesday will be dispatched the following week.",
  },
  {
    title: "Holding Orders",
    text: "You may request to hold your box for up to 45 days by contacting our shipping department. Please ensure your goods are shipped before this deadline; we cannot accept responsibility for loss or damage after the 45-day period.",
  },
];

const getCheckoutItemData = (item) => {
  if (item.type === "product") return item.product;
  if (item.type === "combo") return item.combo;
  if (item.type === "preorder") return item.preorder_product;
  if (item.type === "clearance") {
    return item.clearance_product || item.product || item.clearance || null;
  }
  return null;
};

const getCheckoutUnitPrice = (item, data) => {
  if (!data) return 0;
  if (item.type === "preorder") {
    return Number(data.deposit_amount || 0);
  }
  if (item.type === "clearance") {
    return Number(data.selling_price || 0);
  }
  return Number(data.price || 0);
};

const getProductPreorderCreditAmount = (item, data) => {
  if (item?.type !== "product" || !data) return 0;

  const explicitTotal = Number(data.preorder_credit_total);
  if (Number.isFinite(explicitTotal) && explicitTotal > 0) {
    return explicitTotal;
  }

  const availableQty = Number(data.preorder_credit_available_quantity || 0);
  const creditPerUnit = Number(data.preorder_credit_per_unit || 0);
  const calculatedTotal = availableQty * creditPerUnit;

  return Number.isFinite(calculatedTotal) && calculatedTotal > 0
    ? calculatedTotal
    : 0;
};

const getCheckoutOrderId = (data) =>
  data?.preorder_order_id ||
  (data?.type === "preorder" ? data?.id : null) ||
  data?.local_order_id ||
  data?.order_id ||
  data?.orderId ||
  null;

const hasPreorderOrderItems = (data) =>
  Array.isArray(data?.items) &&
  data.items.some(
    (item) => item?.preorder_product || item?.preorder_product_id
  );

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, clearCart } = useCart();

  const [loading, setLoading] = useState(false);
  const [errorDialog, setErrorDialog] = useState({
    open: false,
    message: "",
  });
  const [profileDialog, setProfileDialog] = useState({
    open: false,
    title: "",
    message: "",
  });
  const [paymentMethod, setPaymentMethod] = useState("razorpay");
  const [qrDetails, setQrDetails] = useState(null);
  const [qrProofFile, setQrProofFile] = useState(null);
  const [qrReference, setQrReference] = useState("");
  const [qrSubmitting, setQrSubmitting] = useState(false);
  const [qrSubmitted, setQrSubmitted] = useState(false);
  const [qrSubmitState, setQrSubmitState] = useState("idle");
  const [qrSubmitMessage, setQrSubmitMessage] = useState("");
  const [qrSuccessOrderId, setQrSuccessOrderId] = useState(null);
  const [qrOrderDetails, setQrOrderDetails] = useState(null);
  const [qrOrderLoading, setQrOrderLoading] = useState(false);
  const [membershipState, setMembershipState] = useState({
    loading: true,
    isMember: false,
  });
  const [upiCopied, setUpiCopied] = useState(false);
  const hasPreorderItems = useMemo(
    () => Array.isArray(cartItems) && cartItems.some((item) => item.type === "preorder"),
    [cartItems]
  );
  const hasNonPreorderItems = useMemo(
    () => Array.isArray(cartItems) && cartItems.some((item) => item.type !== "preorder"),
    [cartItems]
  );
  const isHiddenPreorderMemberFlow =
    !membershipState.loading &&
    membershipState.isMember &&
    hasPreorderItems &&
    !hasNonPreorderItems;
  const shouldHideMemberPreorderPrices =
    !membershipState.loading &&
    membershipState.isMember &&
    hasPreorderItems;
  const formatZeroCurrency = "₹0.00";

  const openProfileDialog = ({ missingPersonal, missingAddress }) => {
    let message = "Please update your profile before placing your order.";

    if (missingPersonal && missingAddress) {
      message =
        "Please add your Personal Information and Address Information in Profile before placing your order.";
    } else if (missingPersonal) {
      message =
        "Please add your Personal Information in Profile before placing your order.";
    } else if (missingAddress) {
      message =
        "Please add your Address Information in Profile before placing your order.";
    }

    setProfileDialog({
      open: true,
      title: "Profile Details Required",
      message,
    });
  };

  /* ===============================
     CALCULATIONS
  =============================== */
  const { subtotal, taxTotal, preorderCreditTotal, grandTotal } = useMemo(() => {
    if (!cartItems || cartItems.length === 0) {
      return {
        subtotal: 0,
        taxTotal: 0,
        preorderCreditTotal: 0,
        grandTotal: 0,
      };
    }

    const subtotalCalc = cartItems.reduce((sum, item) => {
      const data = getCheckoutItemData(item);
      return sum + getCheckoutUnitPrice(item, data) * item.quantity;
    }, 0);

    const taxCalc = cartItems.reduce((sum, item) => {
      const data = getCheckoutItemData(item);
      return sum + Number(data?.product_tax || 0) * item.quantity;
    }, 0);

    const preorderCreditCalc = cartItems.reduce((sum, item) => {
      const data = getCheckoutItemData(item);
      return sum + getProductPreorderCreditAmount(item, data);
    }, 0);

    return {
      subtotal: subtotalCalc,
      taxTotal: taxCalc,
      preorderCreditTotal: preorderCreditCalc,
      grandTotal: Math.max(
        subtotalCalc + taxCalc - preorderCreditCalc,
        0
      ),
    };
  }, [cartItems]);

  useEffect(() => {
    let mounted = true;

    fetchProfileDashboard()
      .then((profile) => {
        if (!mounted) return;
        setMembershipState({
          loading: false,
          isMember: resolveIsMember(profile),
        });
      })
      .catch(() => {
        if (!mounted) return;
        setMembershipState({ loading: false, isMember: false });
      });

    return () => {
      mounted = false;
    };
  }, []);

  /* ===============================
     PAYMENT HANDLER
  =============================== */
  const handlePayment = async () => {
    try {
      setLoading(true);
      setErrorDialog({ open: false, message: "" });
      const profile = await fetchProfileDashboard();
      const isMember = resolveIsMember(profile);
      setMembershipState({ loading: false, isMember });

      if (paymentMethod === "qr" && !isMember) {
        setLoading(false);
        setErrorDialog({
          open: true,
          message: "UPI QR is available for members only.",
        });
        return;
      }

      if (paymentMethod === "razorpay" && isMember) {
        setLoading(false);
        setErrorDialog({
          open: true,
          message: "Razorpay is not available for members. Please use UPI QR.",
        });
        return;
      }

      const missingPersonal = !hasRequiredPersonalInfo(profile?.personal_info);
      const missingAddress = !hasRequiredAddressInfo(profile?.default_address);

      if (missingPersonal || missingAddress) {
        openProfileDialog({ missingPersonal, missingAddress });
        setLoading(false);
        return;
      }

      if (hasPreorderItems && hasNonPreorderItems) {
        setLoading(false);
        setErrorDialog({
          open: true,
          message: "Checkout supports one preorder product at a time.",
        });
        return;
      }

      const order = await (hasPreorderItems
        ? createPreorderOrder({
            payment_method: paymentMethod,
          })
        : createProductOrder({
        payment_method: paymentMethod,
          }));

      const resolvedOrderId = getCheckoutOrderId(order);

      if (paymentMethod === "razorpay") {
        if (!window.Razorpay) {
          throw new Error(
            "Payment gateway is still loading. Please try again."
          );
        }

        const options = {
          key: order.key,
          amount: order.amount,
          currency: "INR",
          name: "DiecastZone",
          description: "Order Payment",
          order_id: order.order_id,

          handler: function () {
            // Move to processing screen
            if (!resolvedOrderId) {
              setLoading(false);
              setErrorDialog({
                open: true,
                message: "Order created, but order ID is missing from the payment response.",
              });
              return;
            }
            navigate(
              `/payment-processing/${resolvedOrderId}${
                hasPreorderItems && !hasNonPreorderItems ? "?type=preorder" : ""
              }`
            );
          },

          modal: {
            ondismiss: function () {
              setLoading(false);
              setErrorDialog({
                open: true,
                message: "Payment was cancelled.",
              });
            },
          },

          theme: {
            color: "#FFA726",
          },
        };

        const rzp = new window.Razorpay(options);

        rzp.on("payment.failed", function (response) {
          const failureMessage =
            response?.error?.description ||
            response?.error?.reason ||
            response?.error?.step ||
            response?.error?.source ||
            "Payment failed. Please try again.";
          setLoading(false);
          setErrorDialog({
            open: true,
            message: failureMessage,
          });
        });

        rzp.open();
      } else if (isHiddenPreorderMemberFlow) {
        setQrDetails(order);
        setQrProofFile(null);
        setQrReference("");
        setQrSubmitted(false);
        setQrSubmitting(true);
        setQrSubmitState("submitting");
        setQrSubmitMessage("Submitting payment proof...");
        setQrSuccessOrderId(null);
        setQrOrderDetails(null);
        setQrOrderLoading(false);

        const response = await submitPreorderQrPaymentProof(null, "");
        const mergedResponse = { ...order, ...response };

        setQrDetails(mergedResponse);
        setQrSubmitted(true);
        setQrSubmitting(false);
        clearCart();
        setQrSubmitState("success");
        setQrSubmitMessage("Payment proof received.");

        const finalOrderId =
          getCheckoutOrderId(response) || resolvedOrderId;
        setQrSuccessOrderId(finalOrderId || null);
        setQrOrderDetails(null);

        if (finalOrderId) {
          setQrOrderLoading(true);
          fetchPreorderOrderDetails(finalOrderId)
            .then((preorderOrder) => setQrOrderDetails(preorderOrder))
            .catch(() => setQrOrderDetails(null))
            .finally(() => setQrOrderLoading(false));
        } else {
          setQrOrderLoading(false);
        }

        setLoading(false);
      } else {
        setQrDetails(order);
        setQrProofFile(null);
        setQrReference("");
        setQrSubmitted(false);
        setQrSubmitState("idle");
        setQrSubmitMessage("");
        setQrSuccessOrderId(null);
        setQrOrderDetails(null);
        setQrOrderLoading(false);
        setLoading(false);
      }

    } catch (err) {
      setLoading(false);
      setQrSubmitting(false);
      console.error("Checkout payment error:", err);
      setErrorDialog({
        open: true,
        message:
          err?.detail ||
          err?.error ||
          err?.message ||
          "Unable to initiate payment.",
      });
    }
  };

  const handleQrProofSubmit = async () => {
    const submitPreorderProof = isPreorderQrFlow;
    const paymentId =
      qrDetails?.payment_id || qrDetails?.paymentId || null;

    if (!submitPreorderProof && !paymentId) {
      setErrorDialog({
        open: true,
        message: "Missing payment ID for this QR transaction.",
      });
      return;
    }

    try {
      setQrSubmitting(true);
      setErrorDialog({ open: false, message: "" });
      setQrSubmitState("submitting");
      setQrSubmitMessage("Submitting payment proof...");
      const fallbackOrderId =
        getCheckoutOrderId(qrDetails);
      const response = submitPreorderProof
        ? await submitPreorderQrPaymentProof(
            qrProofFile,
            qrReference.trim()
          )
        : await submitQrPaymentProof(
            paymentId,
            qrProofFile,
            qrReference.trim()
          );
      setQrDetails((prev) => ({ ...prev, ...response }));
      setQrSubmitted(true);
      setQrSubmitting(false);
      clearCart();
      setQrSubmitState("success");
      setQrSubmitMessage("Payment proof received.");

      const resolvedOrderId =
        getCheckoutOrderId(response) || fallbackOrderId;
      setQrSuccessOrderId(resolvedOrderId || null);
      setQrOrderDetails(null);
      if (resolvedOrderId) {
        setQrOrderLoading(true);
        const fetchOrderDetails = submitPreorderProof
          ? fetchPreorderOrderDetails
          : fetchOrderById;
        fetchOrderDetails(resolvedOrderId)
          .then((order) => setQrOrderDetails(order))
          .catch(() => setQrOrderDetails(null))
          .finally(() => setQrOrderLoading(false));
      } else {
        setQrOrderLoading(false);
      }
    } catch (err) {
      setQrSubmitting(false);
      const message =
        err?.detail ||
        err?.error ||
        err?.message ||
        "Unable to submit payment proof.";
      setQrSubmitState("error");
      setQrSubmitMessage(message);
      setTimeout(() => {
        setQrSubmitState("idle");
        setQrSubmitMessage("");
      }, 2500);
    }
  };

  useEffect(() => {
    if (paymentMethod !== "qr") {
      setQrDetails(null);
      setQrProofFile(null);
      setQrReference("");
      setQrSubmitted(false);
      setQrSubmitting(false);
      setQrSubmitState("idle");
      setQrSubmitMessage("");
      setQrSuccessOrderId(null);
      setQrOrderDetails(null);
      setQrOrderLoading(false);
    }
  }, [paymentMethod]);

  useEffect(() => {
    if (membershipState.loading) return;

    if (membershipState.isMember && paymentMethod !== "qr") {
      setPaymentMethod("qr");
      setQrDetails(null);
      setQrProofFile(null);
      setQrReference("");
      setQrSubmitted(false);
      setQrSubmitting(false);
      setQrSubmitState("idle");
      setQrSubmitMessage("");
      setQrSuccessOrderId(null);
      setQrOrderDetails(null);
      setQrOrderLoading(false);
    }

    if (!membershipState.isMember && paymentMethod !== "razorpay") {
      setPaymentMethod("razorpay");
      setQrDetails(null);
      setQrProofFile(null);
      setQrReference("");
      setQrSubmitted(false);
      setQrSubmitting(false);
      setQrSubmitState("idle");
      setQrSubmitMessage("");
      setQrSuccessOrderId(null);
      setQrOrderDetails(null);
      setQrOrderLoading(false);
    }
  }, [membershipState.isMember, membershipState.loading, paymentMethod]);

  useEffect(() => {
    if (!upiCopied) return;
    const timer = setTimeout(() => setUpiCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [upiCopied]);

  const qrTextValue = useMemo(
    () => getQrTextValue(qrDetails),
    [qrDetails]
  );
  const qrUpiId = useMemo(
    () => getFirstStringValue(qrDetails?.qr_upi_id, qrTextValue),
    [qrDetails, qrTextValue]
  );
  const qrPayeeName = useMemo(
    () => getQrPayeeName(qrDetails),
    [qrDetails]
  );
  const qrContactNumber = useMemo(
    () => getQrContactNumber(qrDetails),
    [qrDetails]
  );
  const qrPaymentId =
    qrDetails?.payment_id || qrDetails?.paymentId || null;
  const qrOrderId = getCheckoutOrderId(qrDetails);
  const isPreorderQrFlow =
    Boolean(qrDetails?.preorder_order_id) ||
    qrDetails?.type === "preorder" ||
    qrOrderDetails?.type === "preorder" ||
    hasPreorderOrderItems(qrDetails) ||
    hasPreorderOrderItems(qrOrderDetails) ||
    (hasPreorderItems && !hasNonPreorderItems);
  const canSubmitQrProof = isPreorderQrFlow
    ? Boolean(qrDetails)
    : Boolean(qrPaymentId);
  const handleCheckoutPrimaryAction = () => {
    if (isHiddenPreorderMemberFlow && qrDetails && !qrSubmitted) {
      handleQrProofSubmit();
      return;
    }

    handlePayment();
  };
  const isCheckoutPrimaryDisabled =
    loading ||
    qrSubmitting ||
    qrSubmitted ||
    (paymentMethod === "qr" && qrDetails && !isHiddenPreorderMemberFlow);

  const handleCopyUpi = async () => {
    if (!qrUpiId) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(qrUpiId);
        setUpiCopied(true);
      } else {
        throw new Error("Clipboard not available");
      }
    } catch (err) {
      setErrorDialog({
        open: true,
        message: "Unable to copy. Please copy the UPI ID manually.",
      });
    }
  };

  const buildWhatsAppMessage = () => {
    const orderId = qrSuccessOrderId || qrOrderId;
    if (!orderId) return "";

    const paymentId = qrPaymentId;
    const amount = qrDetails?.amount;
    const formattedAmount = amount ? `₹${amount}` : "";
    const itemLines = isPreorderQrFlow
      ? (
          Array.isArray(qrOrderDetails?.items) && qrOrderDetails.items.length
            ? qrOrderDetails.items
            : Array.isArray(qrDetails?.items)
              ? qrDetails.items
              : []
        ).map((item) => {
          const name =
            item?.preorder_product_name ||
            item?.preorder_product?.name ||
            qrDetails?.preorder_product_name ||
            "Pre-Order Item";
          const qty = Number(item?.quantity || 1);
          const totalAmount = Number(item?.total_amount || 0);
          const priceLabel =
            Number.isFinite(totalAmount) && totalAmount > 0
              ? ` (₹${totalAmount})`
              : "";
          return `- ${name} x${qty}${priceLabel}`;
        })
      : (Array.isArray(qrOrderDetails?.items) ? qrOrderDetails.items : []).map(
          (item) => {
            const name = item?.item?.name || item?.name || "Item";
            const qty = Number(item?.quantity || item?.qty || 1);
            const unitPrice = Number(item?.unit_price || item?.unitPrice || 0);
            const lineTotal =
              Number.isFinite(unitPrice) && Number.isFinite(qty)
                ? unitPrice * qty
                : null;
            const priceLabel =
              Number.isFinite(lineTotal) && lineTotal > 0
                ? ` (₹${lineTotal})`
                : "";
            return `- ${name} x${qty}${priceLabel}`;
          }
        );

    const orderUrl =
      typeof window !== "undefined"
        ? `${window.location.origin}/order-confirmation/${orderId}${
            isPreorderQrFlow ? "?type=preorder" : ""
          }`
        : "";

    const lines = [
      "Order Confirmation",
      `Order ID: ${orderId}`,
      paymentId ? `Payment ID: ${paymentId}` : null,
      formattedAmount ? `Amount: ${formattedAmount}` : null,
      itemLines.length ? "Items:" : null,
      ...itemLines,
      orderUrl ? `Order Details: ${orderUrl}` : null,
    ].filter(Boolean);

    return lines.join("\n");
  };

  const handleShareWhatsApp = () => {
    const message = buildWhatsAppMessage();
    if (!message) {
      setErrorDialog({
        open: true,
        message: "Order details are not available yet.",
      });
      return;
    }

    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleViewOrder = () => {
    const orderId = qrSuccessOrderId || qrOrderId;
    if (!orderId) return;
    navigate(
      `/order-confirmation/${orderId}${
        isPreorderQrFlow ? "?type=preorder" : ""
      }`
    );
  };

  const handleCloseQrStatus = () => {
    const orderId = qrSuccessOrderId || qrOrderId;
    if (orderId) {
      navigate(
        `/order-confirmation/${orderId}${
          isPreorderQrFlow ? "?type=preorder" : ""
        }`
      );
      return;
    }
    setQrSubmitState("idle");
    setQrSubmitMessage("");
  };

  const isCartEmpty = !cartItems || cartItems.length === 0;

  /* ===============================
     RENDER
  =============================== */
  return (
    <div className="checkout-container">

      {/* ERROR DIALOG */}
      {profileDialog.open && (
        <div className="checkout-dialog-backdrop">
          <div className="checkout-dialog">
            <h3>{profileDialog.title}</h3>
            <p>{profileDialog.message}</p>
            <div className="checkout-dialog-actions">
              <button
                className="checkout-btn-secondary"
                onClick={() =>
                  setProfileDialog({
                    open: false,
                    title: "",
                    message: "",
                  })
                }
              >
                Close
              </button>
              <button
                className="checkout-btn-primary"
                onClick={() => {
                  setProfileDialog({
                    open: false,
                    title: "",
                    message: "",
                  });
                  navigate("/profile");
                }}
              >
                Go to Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {errorDialog.open && (
        <div className="checkout-dialog-backdrop">
          <div className="checkout-dialog">
            <h3>Payment Error</h3>
            <p>{errorDialog.message}</p>
            <div className="checkout-dialog-actions">
              <button
                className="checkout-btn-secondary"
                onClick={() =>
                  setErrorDialog({ open: false, message: "" })
                }
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {qrSubmitState !== "idle" && (
        <div className="checkout-status-backdrop">
          <div className="checkout-status-card">
            {qrSubmitState === "submitting" && (
              <>
                <div className="checkout-status-circle loading">
                  <div className="checkout-status-spinner" />
                </div>
                <h4>Uploading Proof</h4>
                <p className="checkout-status-message">
                  {qrSubmitMessage}
                </p>
              </>
            )}

            {qrSubmitState === "success" && (
              <>
                <div className="checkout-status-circle success">
                  ✓
                </div>
                <h4>Payment Proof Received</h4>
                <p className="checkout-status-message">
                  {qrSubmitMessage}
                </p>
                {qrOrderLoading && (
                  <p className="checkout-status-message">
                    Preparing order details...
                  </p>
                )}
                <div className="checkout-status-actions">
                  <button
                    type="button"
                    className="checkout-status-btn"
                    onClick={handleViewOrder}
                    disabled={!qrSuccessOrderId && !qrOrderId}
                  >
                    View Order
                  </button>
                  <button
                    type="button"
                    className="checkout-status-btn whatsapp"
                    onClick={handleShareWhatsApp}
                    disabled={!qrSuccessOrderId && !qrOrderId}
                  >
                    Send on WhatsApp
                  </button>
                </div>
                <button
                  type="button"
                  className="checkout-status-link"
                  onClick={handleCloseQrStatus}
                >
                  Close
                </button>
              </>
            )}

            {qrSubmitState === "error" && (
              <>
                <div className="checkout-status-circle error">
                  ✕
                </div>
                <h4>Submission Failed</h4>
                <p className="checkout-status-message">
                  {qrSubmitMessage}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {isCartEmpty ? (
        <p className="checkout-center">
          Your cart is empty
        </p>
      ) : (
        <>
          <h3 className="checkout-title">
            YOUR ORDER SUMMARY
          </h3>

          {/* ORDER SUMMARY */}
          <section className="checkout-summary">
            <div className="checkout-summary-header">
              <span>ITEM</span>
              <span>QTY</span>
              <span>UNIT PRICE</span>
              <span>TAX</span>
            </div>

            {cartItems.map((item, index) => {
              const data = getCheckoutItemData(item);
              const unitPrice = getCheckoutUnitPrice(item, data);
              const hideRowPrice =
                shouldHideMemberPreorderPrices &&
                item.type === "preorder";

              return (
                <div className="checkout-row" key={index}>
                  <div className="checkout-item">
                    <img
                      src={data?.image_url || data?.image || "https://picsum.photos/200"}
                      alt={data?.name || "Product"}
                    />
                    <div>
                      <p className="checkout-product">
                        {data?.name || "Product"}
                        {item.type === "combo" && (
                          <span className="checkout-combo">
                            {" "}
                            (Combo)
                          </span>
                        )}
                        {item.type === "preorder" && (
                          <span className="checkout-combo">
                            {" "}
                            (Pre-Order)
                          </span>
                        )}
                        {item.type === "clearance" && (
                          <span className="checkout-combo">
                            {" "}
                            (Clearance)
                          </span>
                        )}
                      </p>
                      <p className="checkout-brand">
                        Brand :{" "}
                        {item.type === "clearance"
                          ? "Clearance Sale"
                          : data?.category?.name || "—"}
                      </p>
                    </div>
                  </div>

                  <div>{item.quantity}</div>
                  <div>{hideRowPrice ? formatZeroCurrency : `₹${unitPrice}`}</div>
                  <div>{hideRowPrice ? formatZeroCurrency : `₹${data?.product_tax || 0}`}</div>
                </div>
              );
            })}

            {!isHiddenPreorderMemberFlow && (
              <div className="checkout-totals">
                <div>
                  <span>Subtotal</span>
                  <span>₹{subtotal}</span>
                </div>
                <div>
                  <span>Total Tax</span>
                  <span>₹{taxTotal}</span>
                </div>
                {preorderCreditTotal > 0 && (
                  <div>
                    <span>Deposited Amount</span>
                    <span>-₹{preorderCreditTotal}</span>
                  </div>
                )}
                <div className="checkout-grand">
                  <span>Total</span>
                  <span>₹{grandTotal}</span>
                </div>
              </div>
            )}
          </section>

          {!membershipState.loading && (
            <>
              <h3 className="checkout-title">
                {membershipState.isMember
                  ? "PREMIUM MEMBERS STORE POLICY & SHIPPING GUIDELINES"
                  : "STORE POLICY & SHIPPING GUIDELINES FOR CLEARANCE SALE"}
              </h3>

              <section className="checkout-policy-box">
                <div className="checkout-policy-list">
                  {(membershipState.isMember
                    ? MEMBER_POLICY_ITEMS
                    : NON_MEMBER_POLICY_ITEMS
                  ).map((item, index) => (
                    <p
                      key={item.title || `${item.text}-${index}`}
                      className="checkout-policy-item"
                    >
                      {item.title ? (
                        <>
                          <strong>{item.title}:</strong> {item.text}
                        </>
                      ) : (
                        item.text
                      )}
                    </p>
                  ))}
                </div>
              </section>
            </>
          )}

          {!isHiddenPreorderMemberFlow && (
            <>
              <h3 className="checkout-title">
                PAYMENT METHOD
              </h3>

              <div className="checkout-payment-methods">
                <label
                  className={`checkout-payment-option ${
                    paymentMethod === "razorpay" ? "is-active" : ""
                  } ${membershipState.isMember ? "is-disabled" : ""}`}
                  onClick={(event) => {
                    if (membershipState.isMember) {
                      event.preventDefault();
                      setErrorDialog({
                        open: true,
                        message:
                          "Razorpay is not available for members. Please use UPI QR.",
                      });
                      setPaymentMethod("qr");
                    }
                  }}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="razorpay"
                    checked={paymentMethod === "razorpay"}
                    onChange={() => setPaymentMethod("razorpay")}
                    disabled={membershipState.isMember}
                  />
                  <div>
                    <span className="checkout-payment-option-title">
                      Razorpay
                      {!membershipState.isMember &&
                        paymentMethod === "razorpay" && (
                          <span className="checkout-badge">
                            Recommended
                          </span>
                        )}
                    </span>
                    <span className="checkout-payment-option-subtitle">
                      {membershipState.isMember
                        ? "Members use UPI QR."
                        : "UPI, cards, netbanking, and wallets."}
                    </span>
                  </div>
                </label>

                <label
                  className={`checkout-payment-option ${
                    paymentMethod === "qr" ? "is-active" : ""
                  } ${!membershipState.isMember ? "is-disabled" : ""}`}
                  onClick={(event) => {
                    if (!membershipState.isMember) {
                      event.preventDefault();
                      setErrorDialog({
                        open: true,
                        message: "UPI QR is available for members only.",
                      });
                      setPaymentMethod("razorpay");
                    }
                  }}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="qr"
                    checked={paymentMethod === "qr"}
                    onChange={() => setPaymentMethod("qr")}
                    disabled={!membershipState.isMember}
                  />
                  <div>
                    <span className="checkout-payment-option-title">
                      UPI QR
                      {membershipState.isMember &&
                        paymentMethod === "qr" && (
                          <span className="checkout-badge">
                            Recommended
                          </span>
                        )}
                    </span>
                    <span className="checkout-payment-option-subtitle">
                      {membershipState.loading
                        ? "Checking membership..."
                        : membershipState.isMember
                          ? "Scan & pay, then upload proof."
                          : "Members only."}
                    </span>
                  </div>
                </label>
              </div>
            </>
          )}

          {paymentMethod === "razorpay" && (
            <section className="checkout-payment-box is-razorpay">
              <div className="checkout-payment-col checkout-payment-left">
                <p>
                  Pay securely with Razorpay in a trusted checkout window.
                </p>
              </div>
              <div className="checkout-payment-col checkout-payment-center">
                <p className="checkout-upi">
                  Pay using UPI, cards, netbanking, or wallets.
                </p>
              </div>
              <div className="checkout-payment-col checkout-payment-right">
                <p className="checkout-upi">
                  Your order confirmation appears right after payment completes.
                </p>
              </div>
            </section>
          )}

      {paymentMethod === "qr" && !isHiddenPreorderMemberFlow && (
        <section className="checkout-payment-box is-qr">
          <div className="checkout-payment-col checkout-payment-left">
            <h4>Scan & Pay</h4>
            <p className="checkout-upi">
              Click "Generate QR" to receive the payment details. Pay using any
              UPI app and upload your payment proof.
            </p>
            <p className="checkout-payment-meta">
              Amount due: ₹{grandTotal}
            </p>
            {qrOrderId && (
              <p className="checkout-payment-meta">
                Order ID: {qrOrderId}
              </p>
            )}
            {qrPaymentId && (
              <p className="checkout-payment-meta">
                Payment ID: {qrPaymentId}
              </p>
            )}
          </div>

          <div className="checkout-payment-col checkout-payment-center">
            {qrDetails ? (
              <div className="checkout-manual-details">
                <div className="checkout-manual-card">
                  <span className="checkout-manual-label">Name</span>
                  <span className="checkout-manual-value">
                    {qrPayeeName || "Rachel Asha"}
                  </span>
                </div>
                <div className="checkout-manual-card">
                  <span className="checkout-manual-label">Number</span>
                  <span className="checkout-manual-value">
                    {qrContactNumber || "8884407888"}
                  </span>
                </div>
                <div className="checkout-manual-card">
                  <span className="checkout-manual-label">UPI ID</span>
                  <span className="checkout-manual-value">
                    {qrUpiId || "—"}
                  </span>
                </div>
                {qrDetails?.qr_image_url && (
                  <div className="checkout-manual-card">
                    <span className="checkout-manual-label">QR Code</span>
                    <img
                      src={qrDetails.qr_image_url}
                      alt="Payment QR"
                      className="checkout-qr-preview"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="checkout-qr-placeholder">
                Payment details will appear here after generation.
              </div>
            )}
          </div>

          <div className="checkout-payment-col checkout-payment-right">
            {qrDetails ? (
              <>
                {canSubmitQrProof ? (
                  <>
                    <label className="checkout-input-label">
                      UTR / Reference (optional)
                      <input
                        type="text"
                        value={qrReference}
                        placeholder="e.g. UTR123456"
                        onChange={(event) =>
                          setQrReference(event.target.value)
                        }
                        disabled={qrSubmitting || qrSubmitted}
                      />
                    </label>
                    <label className="checkout-file-label">
                      Upload payment proof (optional)
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(event) =>
                          setQrProofFile(event.target.files?.[0] || null)
                        }
                        disabled={qrSubmitting || qrSubmitted}
                      />
                    </label>
                    {qrProofFile && (
                      <p className="checkout-file-name">
                        {qrProofFile.name}
                      </p>
                    )}
                    <button
                      className="checkout-proof-btn"
                      onClick={handleQrProofSubmit}
                      disabled={qrSubmitting || qrSubmitted}
                    >
                      {qrSubmitting
                        ? "Submitting..."
                        : qrSubmitted
                          ? "Proof Submitted"
                          : "Submit Payment Proof"}
                    </button>
                    {qrSubmitted && (
                      <p className="checkout-qr-success">
                        Payment proof submitted. We will verify it shortly.
                      </p>
                    )}
                  </>
                ) : (
                  <p className="checkout-upi">
                    Complete the payment using the details shown on the left.
                  </p>
                )}
              </>
            ) : (
              <p className="checkout-upi">
                Generate the Phone Number first to upload proof.
              </p>
            )}
          </div>
        </section>
      )}

          {/* PAYMENT BUTTON */}
          <div className="checkout-footer">
            <button
              className="checkout-complete-btn"
              onClick={handleCheckoutPrimaryAction}
              disabled={isCheckoutPrimaryDisabled}
            >
              
              {isHiddenPreorderMemberFlow
                ? loading || qrSubmitting
                  ? "Submitting..."
                  : qrSubmitted
                    ? "Submitted"
                    : "SUBMIT"
                : loading
                  ? "Processing..."
                  : paymentMethod === "qr"
                    ? qrDetails
                      ? "GPay NUMBER GENERATED"
                      : "GENERATE GPay NUMBER"
                    : "PAY NOW"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Checkout;
